import { CommonModule } from '@angular/common';
import { Component, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { DialogComponentService } from '@merchant-portal/app/components/dialog/dialog.component.service';
import { marked } from 'marked';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { debounceTime, Observable, Subject, take, takeUntil } from 'rxjs';
import { IconsComponent } from 'ui-component/src/lib/icons';
import { ButtonComponent, InputModule, LogoComponent } from 'ui-component/src/public-api';
import { passwordValidator } from '../guard/password.guard.validator';
import { OtpStoreFactory } from '../otp/factory/otp-store.factory';
import { OtpComponent } from '../otp/otp.component';
import { OtpStore } from '../otp/store/otp.store';
import { CreatePinModule } from '../pin/create-pin/create-pin.module';
import { CreatePinStoreFactory } from '../pin/create-pin/factory/create-pin-store';
import { CreatePinStoreService } from '../pin/create-pin/services/create-pin-store.service';
import { LoginInvitationStore } from './login-invitation.store';

@Component({
  selector: 'app-login-invitation',
  templateUrl: './login-invitation.component.html',
  styleUrls: ['./login-invitation.component.scss'],
  providers: [
    LoginInvitationStore,
    {
      provide: CreatePinStoreFactory,
      useClass: CreatePinStoreService,
    },
    {
      provide: OtpStoreFactory,
      useClass: OtpStore,
    },
  ],
  imports: [
    CommonModule,
    CardModule,
    InputTextModule,
    PasswordModule,
    ReactiveFormsModule,
    ButtonModule,
    CreatePinModule,
    OtpComponent,
    LogoComponent,
    InputModule,
    ButtonComponent,
    IconsComponent,
  ],
})
export class LoginInvitationComponent implements OnInit, OnDestroy {
  step$: Observable<'password' | 'pin' | 'otp' | 'tnc' | 'greeting'> =
    this.loginInvitationStore.step$;
  passwordForm: FormGroup;
  startOtp$: Observable<boolean> = this.loginInvitationStore.startOtp$;
  inputType = {
    password: 'password',
    reEnterPassword: 'password',
  };
  confirmPasswordErrMessage = '';
  passwordErrMessage = '';
  username = '';
  merchantName = '';
  fieldFocus = {
    password: false,
    confirmPassword: false,
  };

  isTncAgreed = false;
  isScrolledToBottom = false;
  tncHtml: SafeHtml = '';
  tncTitle = '';
  isTncSigned = false;
  isActivating = false;

  private dialogService: DialogComponentService = inject(DialogComponentService);
  private sanitizer = inject(DomSanitizer);

  private destroy$ = new Subject<void>();
  private activationStarted = false;

  constructor(
    private fb: FormBuilder,
    private loginInvitationStore: LoginInvitationStore,
    private route: ActivatedRoute
  ) {
    this.passwordForm = this.fb.group({
      password: ['', [Validators.required, passwordValidator()]],
      reEnterPassword: ['', [Validators.required, passwordValidator()]],
    });
  }

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const token = params.get('token');
      if (token) {
        this.loginInvitationStore.setToken(token);
        this.loginInvitationStore.verifyLinkInvitation({ token });
      } else {
        this.loginInvitationStore.checkAccessToken();
        this.loginInvitationStore.clearLocalStorage();
      }
    });
    this.getConfirmPasswordError();
    this.getPasswordError();
    this.getUsername();
    this.getMerchantName();
    this.getTncState();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onNextPassword() {
    if (this.passwordForm.valid) {
      this.loginInvitationStore.setPassword(this.passwordForm.value.password);
      if (!this.isTncSigned && this.tncHtml) {
        this.loginInvitationStore.setStep('tnc');
        return;
      }

      this.loginInvitationStore.setStep('pin');
      this.proceedToPin();
    }
  }

  /**
   * Opens the create-PIN dialog and advances to the OTP step.
   * Called after the user accepts the TnC agreement.
   */
  private proceedToPin() {
    this.loginInvitationStore.setStartOtp(true);
    this.dialogService
      .openNewPINDialog({
        height: 'fit-content',
        width: '450px',
        data: {
          mode: 'create-new',
          shouldSkipDirectCreation: true,
          shouldSkipSuccessInfo: true,
        },
      })
      .onClose.pipe(take(1))
      .subscribe(result => {
        if (!result.isSuccess) {
          return;
        }

        this.loginInvitationStore.setPin(result.newPin);
        this.loginInvitationStore.setStep('otp');
      });
  }

  /**
   * "I Agree & Continue" handler. Advances to the PIN step without
   * signing — the sign endpoint is called later, after the user is
   * activated (see the onActivated$ handler), since it requires the
   * access token obtained during activation.
   */
  onAgreeTnc() {
    this.proceedToPin();
  }

  onCancelTnc() {
    this.loginInvitationStore.setStep('password');
    this.isTncAgreed = false;
    this.isScrolledToBottom = false;
  }

  onTncScroll(event: Event) {
    const el = event.target as HTMLElement;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 2) {
      this.isScrolledToBottom = true;
    }
  }

  /**
   * Bind TNC-related state from the store: signed flag (gates skipping),
   * markdown content (rendered via marked), and loading flags for UI.
   * On a successful sign, advance to the PIN dialog.
   */
  private getTncState() {
    this.loginInvitationStore.isTncSigned$.pipe(takeUntil(this.destroy$)).subscribe(isSigned => {
      this.isTncSigned = isSigned;
    });

    this.loginInvitationStore.tncContent$.pipe(takeUntil(this.destroy$)).subscribe(content => {
      if (!content) {
        return;
      }
      const html = marked.parse(content) as string;
      this.tncHtml = this.sanitizer.bypassSecurityTrustHtml(html);
    });

    this.loginInvitationStore.tncTitle$.pipe(takeUntil(this.destroy$)).subscribe(title => {
      this.tncTitle = title ?? '';
    });

    // Once activation succeeds, sign the TnC (the user agreed earlier;
    // the sign endpoint needs the access token that activation provides),
    // then advance to the greeting step — unless already signed.
    this.loginInvitationStore.onActivated$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!this.isTncSigned) {
        this.loginInvitationStore.signTnc();
      }
      this.loginInvitationStore.setStep('greeting');
    });
  }

  onTncCheckboxChange(event: Event) {
    this.isTncAgreed = (event.target as HTMLInputElement).checked;
  }

  activateUser() {
    if (this.activationStarted) {
      return;
    }
    this.activationStarted = true;
    this.loginInvitationStore.activateUser();
  }

  onPinCreated(event: string) {
    this.loginInvitationStore.setPin(event);
    this.loginInvitationStore.setStep('otp');
  }

  onOtpComplete(event: boolean) {
    if (event) {
      this.activateUser();
    }
  }

  onExplore() {
    this.loginInvitationStore.checkAccessToken();
  }

  toggleInputType(field: 'password' | 'reEnterPassword') {
    this.inputType[field] = this.inputType[field] === 'password' ? 'text' : 'password';
  }

  /**
   * Toggle password visibility for the password field
   */
  togglePasswordVisibility(): void {
    this.toggleInputType('password');
  }

  /**
   * Toggle password visibility for the confirm password field
   */
  toggleConfirmPasswordVisibility(): void {
    this.toggleInputType('reEnterPassword');
  }

  /**
   * Get the appropriate eye icon name for password field
   */
  get passwordEyeIconName(): string {
    return this.inputType.password === 'password' ? 'eye' : 'eye-off';
  }

  /**
   * Get the appropriate eye icon name for confirm password field
   */
  get confirmPasswordEyeIconName(): string {
    return this.inputType.reEnterPassword === 'password' ? 'eye' : 'eye-off';
  }

  getUsername() {
    this.loginInvitationStore.username$.pipe(takeUntil(this.destroy$)).subscribe(username => {
      this.username = username ?? '';
    });
  }

  getMerchantName() {
    this.loginInvitationStore.merchantName$
      .pipe(takeUntil(this.destroy$))
      .subscribe(merchantName => {
        this.merchantName = merchantName ?? '';
      });
  }

  /**
   * Get error message for password field
   */
  getPasswordError() {
    const passwordControl = this.passwordForm.get('password');

    if (!passwordControl) {
      return;
    }

    // Clear error immediately when typing starts
    passwordControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (passwordControl.hasError('required')) {
        this.passwordErrMessage = 'Password is required.';
        return;
      }
      this.passwordErrMessage = '';
    });

    // Validate after debounce
    passwordControl.valueChanges.pipe(debounceTime(500), takeUntil(this.destroy$)).subscribe(() => {
      if (passwordControl.hasError('required')) {
        this.passwordErrMessage = 'Password is required.';
        return;
      }

      if (passwordControl.hasError('invalidPassword') && passwordControl.dirty) {
        this.passwordErrMessage =
          'Password must be at least 8 characters, contain at least 1 uppercase letter, 1 symbol, and 1 number';
        return;
      }
    });
  }

  /**
   * Get error message for confirm password field
   */
  getConfirmPasswordError() {
    const confirmPasswordControl = this.passwordForm.get('reEnterPassword');
    const passwordControl = this.passwordForm.get('password');

    if (!confirmPasswordControl || !passwordControl) {
      return;
    }

    confirmPasswordControl.valueChanges
      .pipe(
        debounceTime(500), // ⏳ wait 500ms after typing
        takeUntil(this.destroy$)
      )
      .subscribe(confirmValue => {
        if (confirmPasswordControl.hasError('required')) {
          this.confirmPasswordErrMessage = 'Password is required.';
          return;
        }

        if (confirmPasswordControl.dirty && confirmValue !== passwordControl.value) {
          this.confirmPasswordErrMessage =
            "Your confirmation password doesn't match the new password";
          return;
        }

        this.confirmPasswordErrMessage = '';
      });
  }

  /**
   * Handle field focus/blur events
   */
  onFieldFocus(field: 'password' | 'confirmPassword', isFocused: boolean): void {
    this.fieldFocus[field] = isFocused;
  }

  @HostListener('window:beforeunload', ['$event'])
  confirmNavigation(event: BeforeUnloadEvent) {
    event.preventDefault();
    event.returnValue = true; // This line is necessary for some browsers
    const confirmationMessage = 'Are you sure you want to leave this page? Your data may be lost.';
    event.returnValue = confirmationMessage; // For older browsers
    return confirmationMessage;
  }
}
